import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Image } from 'foundations-components/transitional/components/Image';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Link } from 'foundations-components/transitional/components/Link';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import PartnerTypeIcon from 'marketplace-ui-solutions-core/components/PartnerTypeIcon';
import * as PartnerTier from 'marketplace-ui-solutions-core/constants/PartnerTier';
import * as PartnerType from 'marketplace-ui-solutions-core/constants/PartnerType';
import * as ProfileTierLabelKey from 'marketplace-ui-solutions-core/constants/ProfileTierLabelKey';
import { getProfileTierLabelI18nKey } from 'marketplace-ui-solutions-core/utils/i18n';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as FilterKey from '../constants/search/FilterKey';
import { getServices } from '../slices/serviceCatalogSlice';
import { clearStorefrontFilters, updateStorefrontFilters } from '../slices/storefrontFiltersSlice';
import { fromFilterStateToLocation } from '../utils/storefrontFilters';

// @ts-expect-error untyped import
import { getCanonicalAppContext } from '../selectors/app';
// @ts-expect-error untyped import
import { getFullLocationUrl } from '../utils/urls';
// @ts-expect-error untyped import
import hubspotLogoUrl from 'bender-url!ecosystem-marketplace-solutions-base/img/hubspot-logo.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderPartnerTypeIcon = (partnerType, tier) => /*#__PURE__*/_jsx(PartnerTypeIcon, {
  alt: unescapedText(getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKey.TITLE)),
  bordered: true,
  partnerType: partnerType,
  size: "medium",
  tier: tier
});
const StorefrontPartnerTiers = () => {
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const catalogServices = useSelector(getServices);
  const isPrerender = getIsPrerenderUserAgent();
  const dispatch = useDispatch();
  const getTierLinkProps = useCallback(tier => {
    const newFilters = {
      [FilterKey.TIER]: [tier]
    };
    const filtersLocation = fromFilterStateToLocation(newFilters, catalogServices);
    if (isPrerender) {
      return {
        href: getFullLocationUrl(filtersLocation, canonicalAppContext)
      };
    } else {
      return {
        onClick: () => {
          dispatch(clearStorefrontFilters());
          dispatch(updateStorefrontFilters({
            updates: newFilters
          }));
        }
      };
    }
  }, [catalogServices, isPrerender, canonicalAppContext, dispatch]);
  const eliteProps = getTierLinkProps(PartnerTier.ELITE);
  const diamondProps = getTierLinkProps(PartnerTier.DIAMOND);
  const platinumProps = getTierLinkProps(PartnerTier.PLATINUM);
  const goldProps = getTierLinkProps(PartnerTier.GOLD);
  return /*#__PURE__*/_jsxs(Grid, {
    children: [/*#__PURE__*/_jsxs(GridItem, {
      size: {
        sm: 12,
        md: 6,
        lg: 5,
        xl: 5
      },
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.subtitle_jsx",
          options: {
            linkProps: {
              external: true,
              href: 'https://knowledge.hubspot.com/resources/hubspot-solutions-directory-review-guidelines'
            }
          },
          elements: {
            Link,
            br: 'br'
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(GridItem, {
      size: {
        sm: 12,
        md: 6,
        lg: 4,
        xl: 4
      },
      offset: {
        sm: 0,
        md: 0,
        lg: 3,
        xl: 3
      },
      children: [/*#__PURE__*/_jsx(Image, {
        alt: unescapedText('ecosystem-marketplace-solutions-ui.storefront.partnerTiers.logoAltText'),
        src: hubspotLogoUrl,
        width: 210,
        marginBlockEnd: "500"
      }), /*#__PURE__*/_jsx(Text, {
        as: "h6",
        marginBlockEnd: "400",
        textTransform: "uppercase",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.programListTitle"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(Link, {
          href: eliteProps.href,
          onClick: eliteProps.onClick,
          children: /*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.ELITE),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.ELITE, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        }), /*#__PURE__*/_jsx(Link, {
          href: diamondProps.href,
          onClick: diamondProps.onClick,
          children: /*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.DIAMOND),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.DIAMOND, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        }), /*#__PURE__*/_jsx(Link, {
          href: platinumProps.href,
          onClick: platinumProps.onClick,
          children: /*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.PLATINUM),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.PLATINUM, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        }), /*#__PURE__*/_jsx(Link, {
          href: goldProps.href,
          onClick: goldProps.onClick,
          children: /*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.GOLD),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.GOLD, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        }), /*#__PURE__*/_jsx(MediaObject, {
          align: "center",
          itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.NONE),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getProfileTierLabelI18nKey(PartnerTier.NONE, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
          })
        }), /*#__PURE__*/_jsx(MediaObject, {
          align: "center",
          itemLeft: renderPartnerTypeIcon(PartnerType.PROVIDER, undefined),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getProfileTierLabelI18nKey(PartnerTier.NONE, PartnerType.PROVIDER, ProfileTierLabelKey.TITLE)
          })
        })]
      })]
    })]
  });
};
export default StorefrontPartnerTiers;